import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,

  useMediaQuery,
  Drawer,
  Button,
} from '@mui/material';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import axios from 'axios';

import { Header } from '../../components/Header';
import { PublicProjectFilter } from './PublicProjectFilter';
import PublicProjectCard from './PublicProjectCard';
import { CDN_ELK_URL, IMAGE_PATH } from '../../components/Constants';
import { TextField } from '@mui/material';
import {
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import { Pagination } from './Pagination';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Chip } from "@mui/material";
import { CLIENT_ID, CLIENT_SECRET } from '../../components/Constants';
import crypto from "crypto-js";
import { decryptAES } from '../../components/CryptoUtils';
import { DATA_DECRYPTION_KEY } from '../../components/Constants';

export default function PublicProject() {
  const isMobile = useMediaQuery('(max-width:900px)');

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filters, setFilters] = useState({});
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openFilter, setOpenFilter] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [openSort, setOpenSort] = useState(false);
  const [isLoading, setIsLoading] = useState(false);




  useEffect(() => {
    if (!openSort) return;

    const closeOnScroll = () => setOpenSort(false);

    window.addEventListener('scroll', closeOnScroll, { passive: true });
    return () => window.removeEventListener('scroll', closeOnScroll);
  }, [openSort]);


  const limit = 10;

  const totalPages = Math.ceil(totalCount / limit);


  // -----------------------------
  // HELPERS
  // -----------------------------
  const groupByParent = (data) =>
    data.reduce((acc, item) => {
      if (!acc[item.parent_name]) acc[item.parent_name] = [];
      acc[item.parent_name].push({
        label: item.subcat_name,
        count: item.project_count,
      });
      return acc;
    }, {});

  const handleFilterChange = (value, checked) => {
    setSelectedFilters((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
  };

  // -----------------------------
  // API CALLS
  // -----------------------------
  const fetchFilterOption = async () => {
    try {
      setIsLoading(true); // start loading
      const ts = Math.floor(Date.now() / 1000); // Get the current Unix timestamp
      const hmacStr =
          CLIENT_SECRET +
          CLIENT_ID +
          ts.toString();

      const hmacHash = crypto.SHA256(hmacStr).toString(crypto.enc.Hex);
      const clientIdBtoa = btoa(CLIENT_ID);


      const res = await axios.get(
        `${CDN_ELK_URL}/projects_cat/list`,    
      {   
        params: {
          limit:500,
          offset:0,
          client_id: clientIdBtoa,
          ts,
          hmac: hmacHash,
        }
      }
      );
      const encryptedData = await res.data.data;
      const decryptedText = await decryptAES(
          encryptedData,
          DATA_DECRYPTION_KEY
      );
      const parsed = JSON.parse(decryptedText);
      setFilters(groupByParent(parsed.data));
    } catch (error) {
      console.error('Error fetching filters:', error);
    } finally {
      setIsLoading(false); // stop loading
    }
  };


  const fetchProject = async (page) => {
    try {
      setIsLoading(true); // start loading
      const offset = (page - 1) * limit;

      const ts = Math.floor(Date.now() / 1000); // Get the current Unix timestamp
      const hmacStr =
          CLIENT_SECRET +
          CLIENT_ID +
          ts.toString();

      const hmacHash = crypto.SHA256(hmacStr).toString(crypto.enc.Hex);
          
      // Build search + filters
      const advanceFilterObj = {
        ...(searchText?.trim() && { group_name: [searchText] }),
        ...(selectedFilters.length > 0 && { sub_category: selectedFilters }),
      };

      // Map UI sort to backend
      const sortMap = {
        newest: 'desc',
        oldest: 'asc',
      };
      const order_by = sortMap[sortBy] || 'created_at:desc';
      const clientIdBtoa = btoa(CLIENT_ID);

      // Axios handles URL encoding automatically
      const res = await axios.get(`${CDN_ELK_URL}/projects/list`, {
        params: {
          limit,
          offset,
          advance_filter: JSON.stringify(advanceFilterObj),
          order_by,
          client_id: clientIdBtoa,
          ts,
          hmac: hmacHash,
        },
      });

      const encryptedData = await res.data.data;
      const decryptedText = await decryptAES(
          encryptedData,
          DATA_DECRYPTION_KEY
      );
      

      const parsed = JSON.parse(decryptedText);

      setProjects(parsed.data);
      setTotalCount(parsed.total_hits);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false); // stop loading
    }
  };



  const handleRemoveFilter = (filterToRemove) => {
    setSelectedFilters((prevFilters) =>
      prevFilters.filter((filter) => filter !== filterToRemove)
    );
  };

  useEffect(() => {
    fetchFilterOption();
  }, []);

  useEffect(() => {
    fetchProject(currentPage);
  }, [currentPage, selectedFilters, sortBy, searchText]);

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <>
      <Header />
        <main>

      {/* HEADER OFFSET FIX */}
      <Box sx={{ pt: '80px' }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto', px: 3 }}>
          {/* TITLE */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' }, // stack on mobile
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
              paddingTop:3,
              alignItems: { xs: 'flex-start', md: 'center' },
              flexWrap: 'wrap', // allow wrapping on small screens
            }}
          >
            
            <Typography 
             fontWeight={550} fontSize={28}
            >
              Public Projects Map
              
            </Typography>
            <Box sx={{display:'flex', gap:2, flexWrap:'wrap',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                 width: { xs: "100%", md: "auto" } 
            }}>
            {/* SEARCH */}
            <TextField
              placeholder="Search projects..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              size="small"
              sx={{
                flex: { xs: '1 1 100%', sm: '0 0 448px' }, // grow on mobile, fixed on desktop         
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '& fieldset': { borderColor: '#d1d5db' },
                  '&:hover fieldset': { borderColor: '#047857' },
                  '&.Mui-focused fieldset': { borderColor: '#047857' },
                  '&:hover': { boxShadow: '0 0 0 1px rgba(16,185,129,0.25)' },
                  '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(16,185,129,0.35)' },
                  height: 36,
                },
                '& input': { fontSize: 14, padding: '8px 12px' },
              }}
            />

            {/* SORT */}
            {!isMobile && (
            <FormControl
              size="small"
              sx={{
                height: 36,
                width: { xs: '100%', sm: 180 }, // full width on mobile, fixed on desktop
                flexShrink: 0,
              }}
            >
              <Select
                open={openSort}
                inputProps={{
                  'aria-label': 'Sort by',
                }}
                value={sortBy}
                onOpen={() => setOpenSort(true)}
                onClose={() => setOpenSort(false)}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setOpenSort(false);
                }}
                MenuProps={{ disableScrollLock: true }}             
                sx={{
                  '& fieldset': { borderColor: '#d1d5db' },
                  '&:hover fieldset': { borderColor: '#047857' },
                  '&.Mui-focused fieldset': { borderColor: '#047857' },
                  '&:hover': { boxShadow: '0 0 0 1px rgba(16,185,129,0.25)' },
                  '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(16,185,129,0.35)' },
                  height: 36,
                  color: '#6b7280',

                }}
              >
                <MenuItem value="newest" sx={{color: '#6b7280'}}>Newest</MenuItem>
                <MenuItem value="oldest"sx={{color: '#6b7280'}}>Oldest</MenuItem>
              </Select>
            </FormControl>
             )} 
            </Box>
          </Box>
          {isMobile && (
            <Box
            sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: "#fff",
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 12px",
                gap: 1,
              }}
            >
              {/* Filter */}
              <Button
                fullWidth
                startIcon={<FilterAltIcon />}
                variant="outlined"
                onClick={() => setOpenFilter(true)}
                sx={{
                    color: "#047857",                 // text color (teal)
                    borderColor: "#047857",            // border color
                    "&:hover": {
                      borderColor: "#065f46",          // darker on hover
                      backgroundColor: "rgba(4,120,87,0.05)",
                    },
                  }}
              >
                Filter
              </Button>
              {/* Sort Toggle */}
              <Button
                fullWidth
                startIcon={<SortIcon />}
                variant="outlined"
                sx={{
                  color: "#047857",
                  borderColor: "#047857",
                  "&:hover": {
                    borderColor: "#065f46",
                    backgroundColor: "rgba(4,120,87,0.05)",
                  },
                }}
                onClick={() =>
                  setSortBy((prev) => (prev === "newest" ? "oldest" : "newest"))
                }
              >
                {sortBy === "newest" ? "Newest" : "Oldest"}
              </Button>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 2
            }}
          >
           {selectedFilters.length > 0 && (
              <Button
                size="small"

                onClick={handleClearAll}
                sx={{ ml: 1, fontWeight: 500, color: '#00675B' }}
              >
                Clear all
              </Button>
            )}
            {selectedFilters.map((filter) => (
              <Chip
                key={filter}
                label={filter}
                onDelete={() => handleRemoveFilter(filter)}
                size="small"
                sx={{
                  bgcolor: "background.paper",
                  border: "1px solid",
                  padding: 2,
                  borderColor: "divider",
                  fontWeight: 500,
                  "& .MuiChip-deleteIcon": {
                    opacity: 0.6,
                  },
                  "&:hover": {
                    bgcolor: "action.hover",
                    "& .MuiChip-deleteIcon": {
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}

           
          </Box>

          {/* MAIN LAYOUT */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 4,
            }}
          >
            {/* FILTER SIDEBAR (DESKTOP) */}
            {!isMobile && (
              <Box sx={{ width: 320, flexShrink: 0 }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    position: 'sticky',
                    top: 100,
                  }}
                >
                  <Typography fontWeight={500} mb={3} fontSize={22} >
                    BROWSE BY
                  </Typography>

                  {Object.entries(filters).map(([parent, items]) => (
                    <PublicProjectFilter
                      key={parent}
                      title={parent}
                      items={items}
                      selectedFilters={selectedFilters}
                      onFilterChange={handleFilterChange}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* PROJECT LIST */}
            <Box sx={{
              flex: 1

            }}>
            { projects.length >0  && (
              <Typography
                variant="body2"
                color="text.secondary"
                mb={2}
              >
                Showing {(currentPage - 1) * limit + 1} –{' '}
                {(currentPage - 1) * limit + projects.length} of {totalCount}
              </Typography>
            )}
              {
                isLoading
                  ?
                  <>
                    <div className="col-sm-9">
                      <SkeletonTheme baseColor="#ebebeb" highlightColor="#fff">
                        <div className="row">
                          <Skeleton count={5} />
                          <div className="d-flex align-items-center justify-content-center">
                            <Skeleton className="p-2 m-2 shadow rounded-5" count={1} />
                            <Skeleton className="p-2 m-2 shadow rounded-5" count={1} />
                            <Skeleton className="p-2 m-2 shadow rounded-5" count={1} />
                          </div>
                        </div>
                      </SkeletonTheme>
                    </div>
                  </>
                  :


                  <Stack spacing={3}
                  >
                     { projects.length >0 ? <>
                    {projects.map((project) => (
                      <PublicProjectCard
                        key={project.id}
                        project={project}
                      />
                    ))}
                    </> 
                      : (
                      <div className="no_task">
                        <img src={`${IMAGE_PATH}/not-found.gif`} alt="no-task" />
                          <p>No results found</p>
                      </div>
                          )}
                  </Stack>
              }
                {projects.length > 0 ? (
                  <>
              <Divider sx={{ my: 2 }} />
              <Box
                display="flex"
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                mb={4}
              >
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />              </Box>
                  </>
                ) : null}

                {/* <Box
                display="flex"
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
              >
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={limit}
                  totalItemsCount={totalCount}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  itemClass="page-item"
                  linkClass="page-link"
                  innerClass="pagination"
                />
              </Box> */}
            </Box>
          </Box>
        </Box>
      </Box>
      </main>

      {/* MOBILE FILTER DRAWER */}
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter(false)}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <Typography fontWeight={600} mb={2} >
            Filters
          </Typography>

          {Object.entries(filters).map(([parent, items]) => (
            <PublicProjectFilter
              key={parent}
              title={parent}
              items={items}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
            />
          ))}
        </Box>
      </Drawer>
    </>
  );
}
